import { ProductType, SubscriptionPeriod } from '@meitu/subscribe-core';
import { Product } from '@/types/product';
import { atom } from 'jotai';
import { PeriodTabType } from '@/constants/subscription';
import { curPeriodTabAtom } from './sub';

/**
 * PeriodTabType → ProductType + SubscriptionPeriod 映射
 */
const PERIOD_TAB_FILTER_MAP: Record<
  PeriodTabType,
  { productType: ProductType; subPeriod: SubscriptionPeriod }
> = {
  [PeriodTabType.YEARLY]: {
    productType: ProductType.RenewedSubscription,
    subPeriod: SubscriptionPeriod.PerYear,
  },
  [PeriodTabType.MONTHLY]: {
    productType: ProductType.RenewedSubscription,
    subPeriod: SubscriptionPeriod.PerMonth,
  },
  [PeriodTabType.SINGLE_YEARLY]: {
    productType: ProductType.NonRenewedSubscription,
    subPeriod: SubscriptionPeriod.PerYear,
  },
  [PeriodTabType.SINGLE_MONTHLY]: {
    productType: ProductType.NonRenewedSubscription,
    subPeriod: SubscriptionPeriod.PerMonth,
  },
};

/**
 * 根据 curPeriodTab 从商品列表中筛选出匹配的商品
 */
const filterProductByPeriod = (
  products: Product[],
  periodTab: PeriodTabType
): Product | undefined => {
  const filter = PERIOD_TAB_FILTER_MAP[periodTab];
  if (!filter) return undefined;
  return products.find(
    (item) => item.productType === filter.productType && item.subPeriod === filter.subPeriod
  );
};

/**
 * 该周期下标准或高级会员至少有一条商品（用于新版订阅时长 Tab 是否展示）
 */
export const hasMembershipProductInPeriod = (
  subProducts: Product[],
  proProducts: Product[],
  periodTab: PeriodTabType
): boolean => {
  return (
    !!filterProductByPeriod(subProducts, periodTab) ||
    !!filterProductByPeriod(proProducts, periodTab)
  );
};

/**
 * 商品列表结构标识：仅 id/数量变化视为列表变更，嵌套字段（如 addOn.selected）更新不触发
 */
export const getProductsStructureKey = (products: Product[]): string =>
  `${products.length}:${products.map((item) => item.productId || '').join(',')}`;

/**
 * 加载二维码接口
 */
export const loadingAtom = atom<boolean>(true);

/**
 * 当前选中的商品处于第几位
 */
export const activeIndexAtom = atom<number>(-1);

/**
 * 当前选中的商品信息
 */
export const curProductAtom = atom<Product>((get) => get(productsAtom)[get(activeIndexAtom)]);

/**
 * 商品列表（美豆场景 / 旧版单tab场景使用）
 */
export const productsAtom = atom<Product[]>([]);

/**
 * 标准会员全量商品列表
 */
export const subAllProductsAtom = atom<Product[]>([]);

/**
 * 高级会员全量商品列表
 */
export const proAllProductsAtom = atom<Product[]>([]);

/**
 * 当前时长Tab下的标准会员商品
 */
export const curSubProductAtom = atom<Product | undefined>((get) => {
  const periodTab = get(curPeriodTabAtom);
  const products = get(subAllProductsAtom);
  return filterProductByPeriod(products, periodTab);
});

/**
 * 当前时长Tab下的高级会员商品
 */
export const curProProductAtom = atom<Product | undefined>((get) => {
  const periodTab = get(curPeriodTabAtom);
  const products = get(proAllProductsAtom);
  return filterProductByPeriod(products, periodTab);
});

/**
 * 支付二维码URL
 */
export const qrCodeUrlAtom = atom<string>('');

/**
 * H5 收银台链接（createTradeUrl 返回的 url）
 */
export const payUrlAtom = atom<string>('');

/**
 * 支付成功的商品信息
 */
export const payProductInfoAtom = atom<any>();

/**
 * 席位数
 */
export const seatsAtom = atom<number>(0);
